"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useRouteRestriction } from "@/hooks/useRouteRestriction";
import AccessRestricted from "./AccessRestricted";

interface RouteRestrictionGuardProps {
    children: React.ReactNode;
    redirectTo?: string;
}

/**
 * Component that guards routes for platform practitioners with bookingEngineEnabled === false
 * Redirects to /consultations (or specified route) if user tries to access a restricted route
 * 
 * Safety features:
 * - Prevents redirect loops by ensuring redirectTo is never restricted
 * - Only affects platform practitioners with bookingEngineEnabled === false
 * - Does not interfere with other user types or routes
 */
export function RouteRestrictionGuard({
    children,
    redirectTo = "/consultations"
}: RouteRestrictionGuardProps) {
    const pathname = usePathname();
    const router = useRouter();
    const { isRouteRestricted, isLoading, isRestricted } = useRouteRestriction();

    useEffect(() => {
        // Don't redirect while loading or if pathname is not available
        if (isLoading || !pathname) return;

        // Safety check: Don't redirect if we're already on the redirect target
        // This prevents infinite redirect loops
        if (pathname === redirectTo || pathname.startsWith(redirectTo + "/")) {
            return;
        }

        // Only check restrictions if user is actually restricted
        // This ensures we don't affect non-platform practitioners or those with bookingEngineEnabled === true
        if (isRestricted && isRouteRestricted(pathname)) {
            // Redirect to allowed route
            router.replace(redirectTo);
        }
    }, [pathname, isRouteRestricted, isLoading, redirectTo, router, isRestricted]);

    // Show loading state while checking
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
            </div>
        );
    }

    // Safety check: Don't show restricted message if pathname is null or we're on the redirect target
    if (!pathname) {
        return <>{children}</>;
    }

    if (pathname === redirectTo || pathname.startsWith(redirectTo + "/")) {
        return <>{children}</>;
    }

    // If route is restricted, show access restricted message while redirecting
    if (isRestricted && isRouteRestricted(pathname)) {
        return (
            <AccessRestricted
                title="Access Restricted"
                message="This page is not available for your account type. Redirecting..."
                buttonText="Go to Consultations"
                onGoBack={() => router.push(redirectTo)}
            />
        );
    }

    // Route is allowed, render children
    return <>{children}</>;
}

export default RouteRestrictionGuard;

